(async () => {
  // Parse URL parameters to extract cart data and behaviour
  const urlParams = new URLSearchParams(window.location.search);
  const isSycLink = urlParams.get('syc') === 'true';
  const items = urlParams.get('items');
  const discounts = urlParams.get('discounts');
  const goToCheckout = urlParams.get('checkout') === 'true';
  const cleanUrl = window.location.origin + window.location.pathname;

  // Don't do anything if this isn't an SYC link or no items exist
  if (!isSycLink || !items) {
    return;
  }

  // Show the loading modal while we rebuild the cart
  const loadingModal = document.querySelector('.modal--syc-rebuilder');
  const underlay = document.querySelector('.underlay');

  if (loadingModal && underlay) {
    loadingModal.classList.add('modal--open');
    underlay.classList.add('underlay--open', 'underlay--above-side-cart');
  }

  const setLoadingState = (isLoading) => {
    if (loadingModal && underlay) {
      loadingModal.classList.toggle('modal--open', isLoading);
      underlay.classList.toggle('underlay--open', isLoading);
      underlay.classList.toggle('underlay--above-side-cart', isLoading);
    }
  };

  const requestJson = async (url, options, errorMessage) => {
    const response = await fetch(url, options);
    let responseData = null;

    try {
      responseData = await response.json();
    } catch (error) {
      responseData = null;
    }

    if (!response.ok) {
      const detail = responseData?.description || responseData?.message || response.statusText;
      throw new Error(`${errorMessage} (${response.status}): ${detail}`);
    }

    return responseData;
  };

  const getLiveCart = async () => {
    if (window.liquidAjaxCart?.cart) {
      return window.liquidAjaxCart.cart;
    }

    return requestJson(`${window.Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: { 'content-type': 'application/json' }
    }, 'Failed to retrieve live cart state');
  };

  const waitForNextPaint = async () => new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

  const dispatchCartUpdated = async () => {
    await waitForNextPaint();

    const cart = await getLiveCart();
    const totalSavings = document.querySelector('[data-total-value-saving-total]')?.dataset.totalValueSavingTotal ?? null;

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        cart,
        totalSavings,
      }
    }));
  };

  const ensureSecureYourCartScriptLoaded = async () => {
    if (customElements.get('secure-your-cart')) {
      return;
    }

    if (!window.__sycScriptLoadPromise) {
      const sycScriptSrc = document.querySelector('script[src*="secure-your-cart.js"]')?.src;

      if (!sycScriptSrc) {
        throw new Error('Secure Your Cart script tag could not be found after rendering the drawer');
      }

      window.__sycScriptLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = sycScriptSrc;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Secure Your Cart script'));

        document.head.appendChild(script);
      });
    }

    await window.__sycScriptLoadPromise;
    await waitForNextPaint();
  };

  const syncCartDrawerState = async () => {
    await waitForNextPaint();
    await ensureSecureYourCartScriptLoaded();
    await dispatchCartUpdated();
  };

  const refreshCartUi = async () => {
    if (!window.liquidAjaxCart?.update) {
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer-v2') || document.querySelector('cart-drawer');

    // Mark this rebuild as a cart interaction so the cart-drawer request guard
    // does not short-circuit the Liquid Ajax Cart refresh.
    cartDrawer?.dispatchEvent(new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }));

    await new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        document.removeEventListener('liquid-ajax-cart:request-end', handleRequestEnd);
        reject(new Error('Timed out while refreshing cart UI'));
      }, 10000);

      const handleRequestEnd = (event) => {
        window.clearTimeout(timeoutId);

        const updateFailed = event.detail?.requestState?.responseData?.ok === false;

        if (updateFailed) {
          reject(new Error('Cart UI refresh failed after rebuild'));
          return;
        }

        resolve();
      };

      document.addEventListener('liquid-ajax-cart:request-end', handleRequestEnd, { once: true });
      window.liquidAjaxCart.update();
    });
  };

  const openCartDrawer = async () => {
    const cartDrawer = document.querySelector('cart-drawer-v2') || document.querySelector('cart-drawer');

    if (!cartDrawer) {
      return;
    }

    if (!cartDrawer.querySelector('secure-your-cart') && document.querySelector('[data-dev-mode]')) {
      console.warn('[SYC Rebuilder] Secure Your Cart markup was not found in the cart drawer after Liquid Ajax Cart refresh');
    }

    if (!cartDrawer.open) {
      cartDrawer?.open?.();
      return;
    }

    cartDrawer.open();
  };

  // Parse the 'items' parameter into an array of item objects
  const itemsArray = items.split(',').map(itemString => {
    // Splits variantId, quantity, and properties
    const [variantId, quantity, ...propertyPairs] = itemString.split(':');

    const itemData = {
      id: parseInt(variantId, 10),
      quantity: parseInt(quantity, 10) || 1,
      properties: {}
    };

    // Parse properties if they exist
    propertyPairs.forEach(pair => {
      if (pair && pair.includes('=')) {
        const [key, value] = pair.split('=');
        // Decode URL-encoded characters (like %20 for spaces)
        itemData.properties[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });

    return itemData;
  });

  const hasInvalidItems = itemsArray.some(item => !Number.isInteger(item.id) || item.id <= 0 || !Number.isInteger(item.quantity) || item.quantity <= 0);

  if (hasInvalidItems || itemsArray.length === 0) {
    console.error('[SYC Rebuilder] Invalid cart items found in permalink URL');
    setLoadingState(false);
    return;
  }

  // Determine if we have any discounts to apply
  const discountsArray = discounts
    ? discounts.split(',').map(code => code.trim()).filter(code => code.length > 0)
    : [];

  try {
    // Nuke the existing cart (avoids having to deal with merging items or ensuring unique properties)
    await requestJson(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' }
    }, 'Failed to clear existing cart');

    // Clear the cart and discount code cookies
    document.cookie = 'cart=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
    document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';

    // Force Shopify to 'mint' a new cart
    await requestJson(`${window.Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: { 'content-type': 'application/json' }
    }, 'Failed to initialise a new cart');

    // Rebuild the cart
    await requestJson(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        items: itemsArray,
      }),
    }, 'Failed to rebuild cart from SYC permalink');

    // Apply discounts (if any)
    if (discountsArray.length > 0) {
      await requestJson(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-radiant-cart-update-source': 'syc-rebuilder'
        },
        body: JSON.stringify({
          discount: discountsArray.join(','),
        }),
      }, 'Failed to reapply cart discounts');
    }

    await refreshCartUi();
    window.history.replaceState({}, document.title, cleanUrl);

    // Go to checkout
    if (goToCheckout) {
      window.location.href = `${window.Shopify.routes.root}checkout`;
      return;
    }

    setLoadingState(false);
    await syncCartDrawerState();
    await openCartDrawer();
  } catch (error) {
    console.error('[SYC Rebuilder] Unable to restore cart from permalink', error);
    setLoadingState(false);
  }
})();
